import { palette } from '../color/palette';
import { palettenext } from '../color/palette-next';
import { fontFamilies, fontWeights } from './fonts';

const inputScale = {
  xs: { height: '32px', paddingX: '12px', paddingY: '6px', fontSize: '12px' },
  sm: { height: '40px', paddingX: '12px', paddingY: '8px', fontSize: '14px' },
  md: { height: '48px', paddingX: '16px', paddingY: '10px', fontSize: '16px' },
  lg: { height: '56px', paddingX: '16px', paddingY: '12px', fontSize: '16px' },
  xl: { height: '64px', paddingX: '20px', paddingY: '14px', fontSize: '18px' },
};

export const tokens = {
  typography: {
    fontFamily: fontFamilies.brand,
    fontWeight: {
      regular: fontWeights.regular,
      demibold: fontWeights.demibold,
      bold: fontWeights.bold,
    },
    // Font family used by headings. Defaults to the body font; the rebrand
    // theme overrides this to Antique Legacy.
    headingFontFamily: fontFamilies.brand,
    heading: {
      '1': {
        fontSize: '40px',
        lineHeight: '52px',
      },
      '2': {
        fontSize: '36px',
        lineHeight: '44px',
      },
      '3': {
        fontSize: '32px',
        lineHeight: '40px',
      },
      '4': {
        fontSize: '28px',
        lineHeight: '36px',
      },
      '5': {
        fontSize: '24px',
        lineHeight: '32px',
      },
      '6': {
        fontSize: '20px',
        lineHeight: '28px',
      },
    },
    text: {
      xsmall: {
        fontSize: '12px',
        lineHeight: '20px',
      },
      small: {
        fontSize: '14px',
        lineHeight: '20px',
      },
      medium: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      large: {
        fontSize: '18px',
        lineHeight: '28px',
      },
      large25: {
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: '400',
      },
    },
  },
  /*
        PLEASE NOTE:

        These space tokens are a work in progress and are subject to revision.
        Please use them with caution!
    */
  space: {
    none: '0px',
    xxsmall: '0.25rem',
    xsmall: '0.5rem',
    small: '0.75rem',
    medium: '1rem',
    medium2: '1.5rem',
    large: '2rem',
    xlarge: '3rem',
    xxlarge: '6rem',

    // 2025 Design System spacing tokens
    // https://www.figma.com/design/FXkrXPU0YKVIIlD9TYQdfR/Paper-Design-System-2025?node-id=1405-100961&t=XnAEZFAMqaHtuEl7-4
    /** 1.5rem = 24px */
    large25: '1.5rem',
    /** 2rem = 32px */
    xlarge25: '2rem',
    /** 4rem = 64px */
    xxlarge25: '4rem',
  },
  component: {
    /** Standard input height used across form fields (the `lg` input size) */
    inputHeight: inputScale.lg.height,
    /**
     * Input size scale shared by all Mantine-derived form controls
     * (see `ui-lib/mantine/components.ts`). `lg` is the webapp default.
     * `paddingY` applies to multiline controls only (Textarea, PillsInput).
     */
    input: inputScale,
  },
  border: {
    radius: {
      small: '2px',
      standard: '4px',
      large: '8px',
      xlarge: '12px',
      xxlarge: '16px',
      xxxlarge: '24px',

      // 2025 Design System border radius tokens
      // https://www.figma.com/design/FXkrXPU0YKVIIlD9TYQdfR/Paper-Design-System-2025?node-id=1795-122354&m=dev
      small25: '8px',
      medium25: '16px',
    },
    width: {
      standard: '1px',
      large: '2px',
    },
    color: {
      neutral: palette.neutral[20],
    },
  },
  shadows: {
    medium: '0px 5px 15px 0px rgba(0, 0, 0, 0.05)',
    dark: '0px 4px 15px 0px #161D2526',
  },
  // Component-shape tokens — what makes a button "buttoned" or a dialog
  // "dialoged". Themable so the rebrand can swap in pill buttons + large-
  // radius dialogs without touching individual components.
  shape: {
    button: {
      // 8px matches the legacy Button `borderRadius="large"` Box prop; rebrand
      // overrides to 999px for a pill silhouette.
      borderRadius: '8px',
    },
    dialog: {
      // 1rem matches the legacy Dialog corner radius; rebrand overrides to 24px.
      borderRadius: '1rem',
      // matches legacy dialogBoxShadow; rebrand overrides to the rebrand shadow.
      shadow: '0px 10px 30px rgba(40, 45, 59, 0.15)',
    },
  },
  /*
        PLEASE NOTE:

        These color tokens are a work in progress and are subject to revision.
        Please use them with caution!
    */
  color: {
    foreground: {
      brandAccent: palette.brand[100],
      brandAccentLight: palette.brand[40],
      caution: palette.yellow[100],
      cautionLight: palette.yellow[10],
      critical: palette.red[100],
      criticalLight: palette.red[10],
      info: palette.blue[100],
      infoLight: palette.blue[10],
      link: palette.blue[100],
      linkLight: palette.blue[40],
      linkHover: palette.blue[70],
      neutral: palette.neutral[90],
      neutralInverted: palette.neutral[3],
      positive: palette.green[100],
      positiveLight: palette.green[10],
      primary: palette.black,
      primaryInverted: palette.white,
      secondary: palettenext.neutral[800],
      tertiary: palettenext.neutral[500],
      secondaryInverted: palette.neutral[20],
      success: palette.green[100],
      // Themable "content" tone — used by typography + iconography that should
      // adapt to the active theme. Default mirrors `primary`; rebrand maps to
      // its warm-toned greyscale.
      contentPrimary: palette.black,
      contentSecondary: palettenext.neutral[800],
      contentTertiary: palettenext.neutral[500],
      contentInverse: palette.white,
    },
    background: {
      body: palette.white,
      // TODO - lift this out of the background tokens
      edit: palette.neutral[3], // the color of the background when editing a card
      neutral: palette.neutral[90],
      neutralLight: palettenext.neutral[50],
      neutralHover: palette.neutral[70],
      neutralActive: palette.neutral[80],
      /* Brand tones */
      brandAccent: palette.brand[100],
      brandLight: palette.brand[50],
      /* Semantic tones */
      caution: palette.yellow[100],
      cautionLight: palettenext.yellow[50],
      cautionHover: palette.yellow[40],
      cautionActive: palette.yellow[70],
      critical: palette.red[100],
      criticalLight: palettenext.red[50],
      criticalHover: palette.red[40],
      criticalActive: palette.red[70],
      info: palette.blue[100],
      infoLight: palettenext.blue[50],
      infoHover: palette.blue[40],
      infoActive: palette.blue[70],
      positive: palette.green[100],
      positiveLight: palettenext.green[50],
      positiveHover: palette.green[40],
      positiveActive: palette.green[70],
      // Themable "surface" backgrounds — page-level fills that should swap
      // when the rebrand theme is applied.
      surface: palette.white,
      cream: palettenext.neutral[50],
      cardWhite: palette.white,
      // Rebrand-only fuchsia used on the new business onboarding side panels.
      // Default mirrors cream so non-rebrand consumers see a sensible fallback.
      potentialPinq: palettenext.neutral[50],
      // Rebrand-only gold accent behind the ELC upsell pill + feature icons.
      // Placeholder name, pending design-system naming. Default mirrors cream so
      // non-rebrand consumers see a fallback.
      placeholderKhaki: palettenext.neutral[50],
    },
    border: {
      focus: palette.black,
      // Themable border tones.
      primary: palette.neutral[20],
      secondary: palette.neutral[10],
      cream: palette.neutral[10],
    },
    // Brand accent tones — currently rebrand-only; defaults fall back to the
    // existing brand colour so non-rebrand consumers don't visually change.
    brand: {
      luckyBreak: palette.brand[100],
      tenThousandCoffees: palette.black,
      cardWhite: palette.white,
    },
    foundation25: {
      primaryA: '#120808',
    },
    foreground25: {
      contentNegative: palettenext.red[400],
      contentPrimary: '#120808',
      contentSecondary: '#4D4D4D',
      contentTertiary: '#8A8A8A',
      contentDisabled: palettenext.neutral[400],
    },
    background25: {
      disabled: '#F4F4F4',
      backgroundPrimaryAHover: '#4D4D4D',
      backgroundPrimaryBHover: '#F8F8F8',
      backgroundPrimaryAPress: '#2E2E2E',
      backgroundPrimaryBPress: '#F4F4F4',
      backgroundLightNegative: '#FAE9E7',
    },
  },
};
